import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import { Stack, Typography } from '@mui/material';

import AppButton from '@/shared/ui/AppButton';

import { SEARCH_MIN_QUERY_LENGTH } from '../../api';

interface Props {
  query: string;
  recentSearches: string[];
  onPickQuery: (query: string) => void;
}

const SearchStartState: FC<Props> = ({ query, recentSearches, onPickQuery }) => {
  const { t } = useTranslation();
  const isTyping = query.trim().length > 0;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap="0.625rem"
      flex={1}
      padding="1.5rem"
      data-testid="search-start-state"
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: '3rem',
          height: '3rem',
          borderRadius: '0.75rem',
          backgroundColor: 'accent.light',
          color: 'accent.main',
        }}
      >
        <SearchIcon />
      </Stack>

      <Typography sx={{ fontSize: '1rem', fontWeight: 500, lineHeight: 1.25, color: 'grey.900' }}>
        {t(recentSearches.length > 0 ? 'search.recent_searches' : 'search.no_recent_searches')}
      </Typography>

      <Typography variant="body2" color="text.secondary" textAlign="center">
        {isTyping
          ? t('search.min_characters', { min: SEARCH_MIN_QUERY_LENGTH })
          : t('search.start_hint')}
      </Typography>

      {recentSearches.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="center"
          gap="0.375rem"
          paddingTop="0.375rem"
        >
          {recentSearches.map((recent) => (
            <AppButton
              key={recent}
              variant="secondary"
              size="medium"
              startIcon={<AccessTimeIcon />}
              onClick={() => onPickQuery(recent)}
              data-testid="search-recent-chip"
            >
              {recent}
            </AppButton>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default SearchStartState;
